import QRCode from "qrcode";
import jsQR from "jsqr";
import { useEffect, useRef, useState } from "react";

const QR_SIZE = 232;

/** The code this device shows for the other device's camera. */
export function QrCode({ bytes }: { bytes: Uint8Array }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [failed, setFailed] = useState(false);
  const payload = Array.from(bytes).join(",");

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    QRCode.toCanvas(
      canvas,
      [{ data: new Uint8ClampedArray(payload.split(",").map(Number)), mode: "byte" }],
      {
        errorCorrectionLevel: "L",
        margin: 2,
        width: QR_SIZE,
        // Fixed dark-on-white is a scanner protocol constraint, not theming:
        // themed/inverted modules break camera decoding in dark mode.
        color: { dark: "#111827", light: "#ffffff" },
      },
      (err) => setFailed(Boolean(err)),
    );
  }, [payload]);

  if (failed) return <p style={quietText}>This code could not be drawn. Use emoji instead.</p>;
  return (
    <canvas
      ref={canvasRef}
      aria-label="Verification code"
      width={QR_SIZE}
      height={QR_SIZE}
      style={{
        width: QR_SIZE,
        height: QR_SIZE,
        borderRadius: "var(--chat-radius-md)",
        background: "white",
      }}
    />
  );
}

/** Live camera that hands the first decoded code to `onScan`. */
export function QrCamera({ onScan }: { onScan: (bytes: Uint8Array) => Promise<void> }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let stream: MediaStream | null = null;
    let frame = 0;
    let handling = false;
    const video = videoRef.current;

    const stopCamera = () => {
      stream?.getTracks().forEach((track) => track.stop());
      if (video?.srcObject === stream) video.srcObject = null;
    };

    async function start() {
      if (!navigator.mediaDevices?.getUserMedia || !video) {
        setError("The camera is not available here. Use emoji instead.");
        return;
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
        });
      } catch {
        setError("Allow camera access in Settings, or use emoji instead.");
        return;
      }
      if (cancelled) {
        stopCamera();
        return;
      }
      video.srcObject = stream;
      try {
        await video.play();
      } catch {
        setError("The camera could not start. Use emoji instead.");
        return;
      }
      const scan = () => {
        if (cancelled) return;
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext("2d", { willReadFrequently: true });
        if (canvas && ctx && video.videoWidth > 0) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const code = jsQR(image.data, image.width, image.height, {
            inversionAttempts: "attemptBoth",
          });
          if (code && !handling) {
            handling = true;
            void onScan(new Uint8Array(code.binaryData)).then(stopCamera, () => {
              if (cancelled) return;
              handling = false;
              setError("That is not a verification code.");
              frame = window.requestAnimationFrame(scan);
            });
            return;
          }
        }
        frame = window.requestAnimationFrame(scan);
      };
      frame = window.requestAnimationFrame(scan);
    }

    void start();
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
      stopCamera();
    };
  }, [onScan]);

  return (
    <>
      <video
        ref={videoRef}
        playsInline
        muted
        aria-label="Camera"
        style={{
          width: QR_SIZE,
          height: QR_SIZE,
          objectFit: "cover",
          borderRadius: "var(--chat-radius-md)",
          background: "var(--chat-surface-raised)",
        }}
      />
      <canvas ref={canvasRef} hidden />
      {error ? (
        <p role="alert" style={quietText}>
          {error}
        </p>
      ) : null}
    </>
  );
}

const quietText: React.CSSProperties = {
  margin: 0,
  color: "var(--chat-fg-secondary)",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-sm-size)",
  lineHeight: "var(--chat-type-body-sm-line)",
  textAlign: "center",
};
